import type { ClauseFlagLane } from '@corner/shared';
import { APP } from '../tokens';

export interface ClauseReviewSetupStripProps {
  lane: ClauseFlagLane | null;
  jurisdiction: string;
  loading: boolean;
  variant?: 'strip' | 'panel';
  onLaneChange: (lane: ClauseFlagLane) => void;
  onJurisdictionChange: (value: string) => void;
  onRun: () => void;
  onDismiss: () => void;
}

export default function ClauseReviewSetupStrip({
  lane,
  jurisdiction,
  loading,
  variant = 'strip',
  onLaneChange,
  onJurisdictionChange,
  onRun,
  onDismiss,
}: ClauseReviewSetupStripProps) {
  const F = APP.fontFamily;
  const isPanel = variant === 'panel';
  return (
    <div
      role="region"
      aria-label="Clause review setup"
      style={{
        flexShrink: 0,
        border: isPanel ? `1px solid ${APP.border}` : 'none',
        borderBottom: isPanel ? `1px solid ${APP.border}` : `1px solid ${APP.border}`,
        borderRadius: isPanel ? 8 : 0,
        backgroundColor: isPanel ? APP.bgCardBody : '#FEFCF9',
        boxShadow: isPanel ? '0 1px 4px rgba(26, 23, 20, 0.06)' : 'none',
        padding: isPanel ? '18px 20px' : '12px 18px',
        fontFamily: F,
        width: isPanel ? 'min(680px, calc(100% - 48px))' : 'auto',
        margin: isPanel ? '32px auto 0' : 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: '1 1 280px', minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1A1714' }}>Clause review</h2>
              <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6B5E52', lineHeight: '17px' }}>
                Pick persona and state, then run the scan on the active document.
              </p>
            </div>
            <button
              type="button"
              aria-label="Close clause review setup"
              onClick={onDismiss}
              style={{
                flexShrink: 0,
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: 18,
                lineHeight: 1,
                color: '#9E9189',
                padding: 4,
              }}
            >
              ×
            </button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9E9189' }}>
              Persona
            </span>
            <div style={{ display: 'flex', gap: 8 }}>
              {(['real_estate', 'founder_operator'] as const).map((value) => {
                const selected = lane === value;
                const label = value === 'real_estate' ? 'Real Estate' : 'Business / Founder';
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => onLaneChange(value)}
                    style={{
                      flex: 1,
                      border: `1px solid ${selected ? '#D97645' : '#E8DED4'}`,
                      borderRadius: 8,
                      padding: '9px 12px',
                      backgroundColor: selected ? '#D97645' : 'transparent',
                      color: selected ? '#FFFFFF' : '#3D3229',
                      fontSize: 13,
                      fontWeight: 500,
                      cursor: 'pointer',
                      fontFamily: F,
                      transition: 'all 0.12s ease',
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#9E9189' }}>
              State
            </span>
            <select
              value={jurisdiction}
              onChange={(e) => onJurisdictionChange(e.target.value)}
              style={{
                maxWidth: isPanel ? 340 : 280,
                border: '1px solid #E8DED4',
                borderRadius: 8,
                padding: '9px 12px',
                fontSize: 13,
                fontFamily: F,
                color: '#3D3229',
                backgroundColor: '#FAFAF8',
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="">Select state</option>
              <option value="US-NJ">New Jersey</option>
              <option value="US-NY">New York</option>
              <option value="US-CA">California</option>
              <option value="US-FL">Florida</option>
              <option value="US-TX">Texas</option>
            </select>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              disabled={loading}
              onClick={onRun}
              style={{
                border: 'none',
                borderRadius: 8,
                padding: '10px 18px',
                backgroundColor: loading ? '#E8DED4' : '#D97645',
                color: '#FFFFFF',
                fontSize: 13,
                fontWeight: 600,
                cursor: loading ? 'default' : 'pointer',
                fontFamily: F,
              }}
            >
              {loading ? 'Starting…' : 'Run review'}
            </button>
            <button
              type="button"
              disabled={loading}
              onClick={onDismiss}
              style={{
                border: '1px solid #E8DED4',
                borderRadius: 8,
                padding: '10px 18px',
                backgroundColor: 'transparent',
                color: '#3D3229',
                fontSize: 13,
                fontFamily: F,
                cursor: loading ? 'default' : 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
